<script lang="ts">
  import { ITERATIONS_BY_PRESET, type PipelineParams, type QualityPreset } from '$lib/types'

  interface Props {
    params: PipelineParams
    disabled?: boolean
  }

  // Bound so edits flow straight back to the owner's state object.
  let { params = $bindable(), disabled = false }: Props = $props()

  let advancedOpen = $state(false)

  // The threshold slider is only meaningful once auto mode is switched off, so
  // the checkbox and the slider are kept as separate pieces of state.
  let autoThreshold = $state(params.threshold === null)
  let manualThreshold = $state(params.threshold ?? 128)

  $effect(() => {
    params.threshold = autoThreshold ? null : manualThreshold
  })

  const presets: QualityPreset[] = ['fast', 'default', 'high']
</script>

<section class="panel" data-testid="parameter-panel">
  <div class="row">
    <label for="resolution">Working resolution</label>
    <select id="resolution" bind:value={params.workingResolution} {disabled} data-testid="param-resolution">
      <option value={512}>512 px</option>
      <option value={768}>768 px</option>
      <option value={1024}>1024 px</option>
    </select>
  </div>

  <div class="row">
    <label for="quality">Quality</label>
    <select id="quality" bind:value={params.quality} {disabled} data-testid="param-quality">
      {#each presets as preset (preset)}
        <option value={preset}>
          {preset[0].toUpperCase() + preset.slice(1)} — {ITERATIONS_BY_PRESET[preset]} iterations
        </option>
      {/each}
    </select>
  </div>

  <div class="row">
    <label for="width-mode">Stroke width</label>
    <select id="width-mode" bind:value={params.widthMode} {disabled} data-testid="param-width-mode">
      <option value="uniform">Uniform</option>
      <option value="variable">Variable</option>
    </select>
  </div>

  <div class="row checkbox">
    <input id="invert" type="checkbox" bind:checked={params.invert} {disabled} data-testid="param-invert" />
    <label for="invert">Invert (white ink on dark paper)</label>
  </div>

  <div class="row checkbox">
    <input
      id="auto-invert"
      type="checkbox"
      bind:checked={params.autoInvert}
      {disabled}
      data-testid="param-auto-invert"
    />
    <label for="auto-invert">
      Detect polarity automatically
      <span class="hint">flips the image when the ink covers most of it</span>
    </label>
  </div>

  <div class="row checkbox">
    <input id="optimize" type="checkbox" bind:checked={params.optimize} {disabled} data-testid="param-optimize" />
    <label for="optimize">Refine with the GPU optimizer</label>
  </div>

  <div class="row checkbox">
    <input
      id="enhanced-init"
      type="checkbox"
      bind:checked={params.enhancedInit}
      {disabled}
      data-testid="param-enhanced-init"
    />
    <label for="enhanced-init">
      Enhanced initialization
      <span class="hint">splits strokes using depth and semantics; downloads models once</span>
    </label>
  </div>

  <details bind:open={advancedOpen} data-testid="advanced-params">
    <summary>Advanced</summary>

    <div class="row checkbox">
      <input id="auto-threshold" type="checkbox" bind:checked={autoThreshold} {disabled} data-testid="param-auto-threshold" />
      <label for="auto-threshold">Automatic threshold (Otsu)</label>
    </div>

    <div class="row">
      <label for="threshold">Threshold</label>
      <input
        id="threshold"
        type="range"
        min="1"
        max="254"
        bind:value={manualThreshold}
        disabled={disabled || autoThreshold}
        data-testid="param-threshold"
      />
      <output>{autoThreshold ? 'auto' : manualThreshold}</output>
    </div>

    <div class="row">
      <label for="min-length">Minimum stroke length</label>
      <input id="min-length" type="number" min="0" max="200" bind:value={params.minStrokeLength} {disabled} data-testid="param-min-length" />
      <output>px</output>
    </div>

    <div class="row">
      <label for="fit-error">Curve fitting tolerance</label>
      <input id="fit-error" type="number" min="0.2" max="10" step="0.1" bind:value={params.fitError} {disabled} data-testid="param-fit-error" />
      <output>px</output>
    </div>

    <div class="row">
      <label for="width-degree">Width polynomial degree</label>
      <input
        id="width-degree"
        type="number"
        min="0"
        max="8"
        bind:value={params.widthDegree}
        disabled={disabled || params.widthMode === 'uniform'}
        data-testid="param-width-degree"
      />
    </div>

    <div class="row">
      <label for="despeckle">Despeckle below</label>
      <input id="despeckle" type="number" min="0" max="500" bind:value={params.despeckle} {disabled} data-testid="param-despeckle" />
      <output>px²</output>
    </div>

    <div class="row checkbox">
      <input id="prune" type="checkbox" bind:checked={params.prune} disabled={disabled || !params.optimize} data-testid="param-prune" />
      <label for="prune">Prune negligible strokes</label>
    </div>

    <div class="row checkbox">
      <input id="simplify" type="checkbox" bind:checked={params.simplify} disabled={disabled || !params.optimize} data-testid="param-simplify" />
      <label for="simplify">Simplify control points</label>
    </div>

    <div class="row checkbox">
      <input id="densify" type="checkbox" bind:checked={params.densify} disabled={disabled || !params.optimize} data-testid="param-densify" />
      <label for="densify">Densify uncovered regions</label>
    </div>

    <div class="row">
      <label for="smoothness">G¹ continuity weight</label>
      <input
        id="smoothness"
        type="number"
        min="0"
        max="10"
        step="0.1"
        bind:value={params.smoothnessWeight}
        disabled={disabled || !params.optimize}
        data-testid="param-smoothness"
      />
    </div>

    <div class="row">
      <label for="curvature">Curvature cap weight</label>
      <input
        id="curvature"
        type="number"
        min="0"
        max="10"
        step="0.1"
        bind:value={params.curvatureWeight}
        disabled={disabled || !params.optimize}
        data-testid="param-curvature"
      />
    </div>

    <div class="row">
      <label for="seed">Random seed</label>
      <input id="seed" type="number" min="0" bind:value={params.seed} {disabled} data-testid="param-seed" />
    </div>
  </details>
</section>

<style>
  .panel {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    padding: 1rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }

  .row {
    display: grid;
    grid-template-columns: 1fr auto auto;
    align-items: center;
    gap: 0.6rem;
  }

  .row.checkbox {
    grid-template-columns: auto 1fr;
  }

  .row.checkbox label {
    order: 2;
  }

  .row.checkbox input {
    order: 1;
    justify-self: start;
  }

  label {
    color: var(--text-muted);
    font-size: 0.9rem;
  }

  select,
  input[type='number'] {
    min-width: 6rem;
    padding: 0.3rem 0.5rem;
    color: inherit;
    background: var(--surface-raised);
    border: 1px solid var(--border-strong);
    border-radius: 6px;
  }

  input[type='range'] {
    min-width: 8rem;
  }

  .hint {
    display: block;
    color: var(--text-muted);
    opacity: 0.75;
    font-size: 0.78rem;
  }

  output {
    min-width: 3rem;
    font-family: var(--mono);
    font-size: 0.8rem;
    color: var(--text-muted);
  }

  details {
    margin-top: 0.25rem;
    padding-top: 0.5rem;
    border-top: 1px solid var(--border);
  }

  summary {
    cursor: pointer;
    color: var(--text-muted);
    font-size: 0.9rem;
  }

  details[open] summary {
    margin-bottom: 0.6rem;
  }

  details > :global(.row + .row) {
    margin-top: 0.6rem;
  }
</style>
